// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { TransactionSummaryItem } from '@client/banks/components/shared/transactions-list';
import { useBankUploadData } from '@client/banks/providers/bank-upload-provider';
import { formatAccountIbanReference } from '@client/banks/utils/account-reference';
import { hasBankUploadListData } from '@client/banks/utils/has-bank-upload-list-data';
import {
  formatTransactionAmount,
  formatTransactionDateLabel,
  getTransactionTitle,
} from '@client/banks/utils/transaction-display';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import produce from 'immer';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const useTransactionsList = () => {
  const { i18n } = useTranslation();
  const { navigate } = useStepsData();
  const { formData, saveFormData, openListEditor } = useBankUploadData();

  const canFinalize = useMemo(() => hasBankUploadListData(formData), [formData]);

  const transactions = useMemo(
    () => formData.transactions ?? [],
    [formData.transactions],
  );
  const isEmpty = useMemo(() => transactions.length === 0, [transactions.length]);

  const items = useMemo<TransactionSummaryItem[]>(
    () =>
      transactions.map((transaction, index) => {
        const meta = [
          formatTransactionDateLabel(transaction.transactionDate, i18n.language),
          formatAccountIbanReference(transaction.iban),
          transaction.currency,
        ].join(' · ');

        return {
          index,
          transaction,
          title: getTransactionTitle(transaction),
          meta,
          amountLabel: formatTransactionAmount(
            transaction.amount,
            transaction.currency,
          ),
        };
      }),
    [i18n.language, transactions],
  );

  const openTransactionForm = useCallback(
    (index?: number) => {
      openListEditor('transactions', index);
      navigate('transactions-form');
    },
    [navigate, openListEditor],
  );

  const onAdd = useCallback(() => {
    openTransactionForm();
  }, [openTransactionForm]);

  const onImportExcel = useCallback(() => {
    navigate('transactions-import');
  }, [navigate]);

  const onEdit = useCallback(
    (index: number) => {
      openTransactionForm(index);
    },
    [openTransactionForm],
  );

  const onDelete = useCallback(
    (index: number) => {
      saveFormData(
        'transactions',
        produce(transactions, draft => {
          draft.splice(index, 1);
        }),
      );
    },
    [saveFormData, transactions],
  );

  return {
    items,
    isEmpty,
    canFinalize,
    onAdd,
    onImportExcel,
    onEdit,
    onDelete,
  };
};
